import { LocalStorageService } from 'angular-2-local-storage';

import { catchError, map, share } from 'rxjs/operators';

import { throwError as observableThrowError, Observable, of, ReplaySubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { SapiService } from '../services/sapi.service';

import { Publication, CustomField, PublicationConfig } from '../interfaces/publication';
import { Role } from './../interfaces/user';

@Injectable()
export class PublicationService {
	locale = 'en-gb';
	public baseUrl: string;
	public mainrollBaseUrl: string;
	public publicationConfig: PublicationConfig;
	public publicationConfigObs = new ReplaySubject<PublicationConfig>(1);

	private publicationData: Publication;
	private publicationDataRequestObs: Observable<any>;

	private rolesData: Role[];
	private rolesDataRequestObs: Observable<any>;
	public publicationObs = new ReplaySubject<Publication>(1);

	private extraPlayoutFields: string[];

	constructor(
		private sapiService: SapiService,
		private localStorageService: LocalStorageService
	) {}

	getConfig(): Observable<any> {
		if (this.publicationConfig) {
			return of(this.publicationConfig);
		}

		return this.sapiService.getUrl('/config').pipe(
			map((res) => {
				this.setConfig(res);
				return res;
			}),
			share()
		);
	}

	updateConfig(): void {
		this.sapiService.getUrl('/config').subscribe(res => {
			this.setConfig(res);
		});
	}

	private setConfig(res: any) {
		this.publicationConfig = res;
		this.publicationConfigObs.next(res);
	}

	getData(forceUpdate = false): Observable<Publication> {
		const publicationIsSameAsStored = this.publicationData &&
			this.publicationData.id === this.localStorageService.get('ovp-currentPublicationId');

		if (!publicationIsSameAsStored) {
			// console.warn('publicationIsSameAsStored', publicationIsSameAsStored);
			this.publicationData = null;
		}

		if (forceUpdate) {
			// might have switched publications so all related calls can't use cache
			this.publicationDataRequestObs = null;
			this.publicationData = null;
			this.rolesDataRequestObs = null;
			this.rolesData = null;
		}

		if (!this.publicationDataRequestObs) {
			this.publicationDataRequestObs = this.sapiService.getUrl('/publication')
				.pipe(
					share(),
					map((res) => {
						this.publicationData = (res as Publication);
						this.extraPlayoutFields = this.publicationData.extraPlayoutFields;
						if (this.publicationData.baseurl) {
							this.baseUrl = this.publicationData.baseurl.replace(/(?:https?:)?\/\//, '');
							this.mainrollBaseUrl = this.baseUrl.match(/\.dev\.|\.acc\.|\.test\./)
								? this.baseUrl
								: this.baseUrl.replace('.bbvms.', '.mainroll.');
							if (this.publicationData.externalBaseUrl) {
								this.baseUrl = this.publicationData.externalBaseUrl;
								this.mainrollBaseUrl = this.publicationData.externalBaseUrl;
							}
						}
						this.localStorageService.set('ovp-currentPublicationId', this.publicationData.id);
						// set for stats ( see singleMediaClipView: publicationUrl )
						this.localStorageService.set('ovp-currentPublicationBaseURL', this.publicationData.baseurl);
						return this.publicationData;
					}),
					catchError((error: any) => {
						this.localStorageService.remove('ovp-currentPublicationId');
						this.localStorageService.remove('ovp-currentPublicationBaseURL');
						this.publicationData = null;
						this.publicationDataRequestObs = null; // make sure we try again
						return of(null);
					})
				);
			this.publicationDataRequestObs.subscribe((data) => {
				this.publicationObs.next(data);
			});
		}

		return this.publicationObs;

	}

	reset() {
		this.publicationObs.next(null);
	}

	getRoles(forceUpdate = false): Observable<Role[]> {
		if (!forceUpdate && this.rolesData) {
			return of(this.rolesData);
		}

		if (forceUpdate || !this.rolesDataRequestObs) {
			this.rolesDataRequestObs = this.sapiService.getUrl('/role')
				.pipe(
					share(),
					map((res: any) => {
						this.rolesData = (res.items as Role[]);
						return this.rolesData;
					}),
					catchError((error: any) => {
						this.rolesDataRequestObs = null; // make sure we try again
						return observableThrowError(error);
					})
				);
		}

		return this.rolesDataRequestObs;

	}

	getCustomFields(entityName: string = 'mediaclip', forceUpdate = false): Observable<CustomField[]> {
		return this.getData(forceUpdate).pipe(
			map((publication: Publication) => {
				if (publication && publication.customfields) {
					return publication.customfields[entityName];
				}
				return [];
			})
		);
	}

	hasPublicationField(permission: string) {
		return this.extraPlayoutFields && this.extraPlayoutFields.indexOf(permission) > -1;
	}
}
